import {
  Object3D,
  Vector3,
  Matrix4,
  WebXRManager,
  XRSession,
  XRReferenceSpace,
  XRFrame,
  XRHitTestSource,
  XRRigidTransform,
  Quaternion,
  XRInputSourceEvent,
  Mesh,
} from 'three';
import { ArilynLibrary } from './ALEvents';
import { Main } from './main';
import loaderAsset from './assets/website/lnx-symbol.svg';
import bannerAsset from './static/banner.png';
import './webxr.css';

export class WebXRTracker {
  private readonly linkClicked = new ArilynLibrary.ALEvent();
  public get LinkClickedEvent() {
    return this.linkClicked.expose();
  }

  private readonly modelAnchored = new ArilynLibrary.ALEvent();
  public get ModelAnchoredEvent() {
    return this.modelAnchored.expose();
  }

  private readonly sessionEnded = new ArilynLibrary.ALEvent();
  public get SessionEndedEvent() {
    return this.sessionEnded.expose();
  }

  main: Main;

  private xr: WebXRManager;
  private currentSession?: XRSession;
  private poseMatrix: Matrix4;
  private currentModel?: Object3D;
  private viewerHitTestSource?: XRHitTestSource;
  private xrRefSpace?: XRReferenceSpace;
  private overlay?: HTMLElement;
  private overlayButton?: HTMLButtonElement;
  private overlayLoader?: HTMLDivElement;
  private overlayLoaderImg?: HTMLImageElement;

  private devicePos: Vector3 = new Vector3();
  private deviceDir: Vector3 = new Vector3();
  private deviceRight: Vector3 = new Vector3();
  private deviceUp: Vector3 = new Vector3();
  private isModelAnchored: boolean = false;
  private targetPos: Vector3 = new Vector3();
  private inputs: InputTransform[] = [];
  private previousInputs: InputTransform[] = [];

  private intersectionPoint?: Vector3;
  private prevIntersectionPoint?: Vector3;
  private currentTwistVector: Vector3 = new Vector3();
  private previousTwistVector: Vector3 = new Vector3();
  private twistCrossVector: Vector3 = new Vector3();
  private previousDragPos: Vector3 = new Vector3();

  constructor(main: Main) {
    if (!main.threeScene) {
      throw new Error('No threeScene in main defined.');
    }

    this.main = main;
    this.xr = main.threeScene.renderer.xr;
    this.xr.enabled = true;
    this.xr.addEventListener('sessionend', this.onSessionEnd.bind(this));
    this.poseMatrix = new Matrix4();
  }

  start() {
    this.overlay = this.createUIOverlay();
    let nav: any = navigator;
    let options = {
      requiredFeatures: ['local', 'hit-test', 'anchors'],
      optionalFeatures: ['dom-overlay'],
      domOverlay: { root: this.overlay },
    };
    nav.xr
      .requestSession('immersive-ar', options)
      .then(this.setupSession.bind(this))
      .catch((e: any) => {
        console.error(e);
        document.documentElement.classList.add('no-ar', 'unsupported-device');
        document.documentElement.classList.remove('has-webxr', 'supported-device');
      });
  }

  sceneLoaded() {
    this.currentModel = this.main.threeScene?.gltfParent;
    this.setVisiblity(false);
  }

  update(frame: XRFrame) {
    if (frame && this.currentSession && this.xrRefSpace && this.currentModel) {
      // Update model position according to anchor
      if (frame.trackedAnchors) {
        frame.trackedAnchors.forEach((anchor) => {
          const anchorPose = frame.getPose(anchor.anchorSpace, this.xrRefSpace as any);
          if (anchorPose) {
            this.poseMatrix.fromArray(anchorPose.transform.matrix);
            this.targetPos.setFromMatrixPosition(this.poseMatrix);
          }
        });
      }

      // Get device position
      let pose = frame.getViewerPose(this.xrRefSpace);
      if (pose) {
        this.calculateDeviceTransform(pose.transform);

        // Hit test for a plane to anchor to
        if (!this.isModelAnchored) {
          if (this.viewerHitTestSource) {
            let hitTestResults = frame.getHitTestResults(this.viewerHitTestSource);
            if (hitTestResults.length > 0) {
              let hitPose = hitTestResults[0].getPose(this.xrRefSpace);
              if (hitPose) {
                this.poseMatrix.fromArray(hitPose.transform.matrix);

                // Check that the hit plane has normal pointing up
                if (this.poseMatrix.elements[5] > 0.75) {
                  this.targetPos.setFromMatrixPosition(this.poseMatrix);

                  // Check that plane is near enough and low enough
                  let distance = this.devicePos.distanceTo(this.targetPos);
                  let height = this.devicePos.y - this.targetPos.y;
                  if (distance < 5 && height > 0.5) {
                    // Rotate content to face camera
                    this.currentModel.position.copy(this.targetPos);
                    this.currentModel.lookAt(this.devicePos.x, this.targetPos.y, this.devicePos.z);

                    // Create an anchor when a horizontal plane is found
                    frame.createAnchor(hitPose.transform, this.xrRefSpace);
                    this.isModelAnchored = true;
                    this.modelAnchored.invoke();

                    // Update UI
                    if (this.overlayButton) this.overlayButton.style.opacity = '1';
                    if (this.overlayLoader) this.overlayLoader.style.opacity = '0';

                    // Update model
                    this.setVisiblity(true);
                  }
                }
              }
            }
          }
        }
      }

      // Lerp model to target pos
      if (this.currentModel.position.distanceTo(this.targetPos) > 0.005) {
        this.currentModel?.position.lerp(this.targetPos, 0.2);
      }

      this.updateInputs(frame);
    }
  }

  private setVisiblity(visible: boolean) {
    if (this.currentModel) {
      this.currentModel.traverse((child) => {
        if (child instanceof Mesh) {
          child.visible = visible;
        }
      });
    }
  }

  private onSessionEnd(_event: any) {
    document.documentElement.classList.remove('loading-ar');
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = undefined;
    }
    this.sessionEnded.invoke();
  }

  private animateOverlayLoader(forward: boolean) {
    if (this.overlayLoaderImg) {
      this.overlayLoaderImg.style.transform = forward
        ? 'translate(-50%, -50%) rotateX(45deg)'
        : 'translate(-50%, -50%) rotateX(0deg)';
      if (this.overlayLoader && this.overlayLoader.style.opacity == '1')
        setTimeout(() => this.animateOverlayLoader(!forward), 1000);
    }
  }

  private createUIOverlay(): HTMLDivElement {
    let element = document.createElement('div');
    element.className = 'overlay';
    this.overlayButton = document.createElement('button');
    this.overlayButton.className = 'overlay-button';
    this.overlayButton.onclick = () => this.linkClicked.invoke();
    let buttonImg = document.createElement('img');
    buttonImg.src = bannerAsset;
    this.overlayButton.append(buttonImg);
    this.overlayLoader = document.createElement('div');
    this.overlayLoader.className = 'overlay-loader';
    this.overlayLoaderImg = document.createElement('img');
    this.overlayLoaderImg.src = loaderAsset;
    this.overlayLoader.append(this.overlayLoaderImg);
    let overlayLoaderText = document.createElement('span');
    overlayLoaderText.className = 'overlay-loader-text';
    overlayLoaderText.innerText =
      'Loading...\nPoint the device down and\nmove it to map the environment';
    this.overlayLoader.append(overlayLoaderText);

    element.append(this.overlayLoader);
    element.append(this.overlayButton);
    document.body.append(element);
    return element;
  }

  private onSessionStart() {
    document.documentElement.classList.remove('loading-ar');
    this.isModelAnchored = false;
    if (this.overlayLoader) {
      this.overlayLoader.style.opacity = '1';
      this.animateOverlayLoader(true);
    }
  }

  private updateInputs(frame: any) {
    if (!this.currentSession) return;
    for (let inputSource of this.currentSession.inputSources) {
      let targetRayPose = frame.getPose(inputSource.targetRaySpace, this.xrRefSpace);
      if (targetRayPose) {
        this.calculateInputTransform(targetRayPose.transform);
      }
    }

    // Single touch
    if (this.inputs.length == 1 && this.previousInputs.length == 0) this.singleInputStarted();
    else if (this.inputs.length == 1 && this.previousInputs.length == 1) this.singleInputMoved();
    else if (this.inputs.length == 0 && this.previousInputs.length == 1) this.singleInputEnded();

    // Two touches
    if (this.inputs.length == 2 && this.previousInputs.length == 2) this.doubleInputMoved();

    this.previousInputs = this.inputs;
    this.inputs = [];
  }

  private singleInputStarted() {
    this.prevIntersectionPoint = this.getIntersectionPoint();
  }

  private singleInputMoved() {
    if (!this.currentModel) return;
    this.intersectionPoint = this.getIntersectionPoint();
    if (this.prevIntersectionPoint && this.intersectionPoint) {
      let delta = this.prevIntersectionPoint.sub(this.intersectionPoint);
      if (delta.length() > 0.2) {
        delta.normalize().multiplyScalar(0.2);
      }
      this.previousDragPos.copy(this.currentModel.children[0].position);
      this.currentModel.children[0].position.sub(delta);
      if (this.currentModel.children[0].position.length() > 10) {
        this.currentModel.children[0].position.copy(this.previousDragPos);
      }
      this.prevIntersectionPoint.copy(this.intersectionPoint);
    }
  }

  private singleInputEnded() {
    this.intersectionPoint = undefined;
    this.prevIntersectionPoint = undefined;
  }

  private doubleInputMoved() {
    this.currentTwistVector.copy(this.inputs[0].pos).sub(this.inputs[1].pos);
    this.previousTwistVector.copy(this.previousInputs[0].pos).sub(this.previousInputs[1].pos);
    let currentAngle = this.angleDiff(
      this.currentTwistVector,
      this.previousTwistVector,
      this.deviceDir
    );
    if (this.currentModel)
      this.currentModel.children[0].rotateOnAxis(this.currentModel.up, currentAngle);
  }

  private angleDiff(vec1: Vector3, vec2: Vector3, normal: Vector3): number {
    let dot = vec1.dot(vec2);
    this.twistCrossVector.crossVectors(vec1, vec2);
    return Math.atan2(this.twistCrossVector.dot(normal), dot);
  }

  private getIntersectionPoint(): Vector3 | undefined {
    let point: Vector3 | undefined = undefined;
    if (this.currentModel) {
      point = this.lineIntersection(
        this.currentModel.position,
        this.currentModel.up,
        this.inputs[0].pos,
        this.inputs[0].dir
      );
    }
    return point;
  }

  private lineIntersection(
    planePoint: Vector3,
    planeNormal: Vector3,
    linePoint: Vector3,
    lineDirection: Vector3
  ): Vector3 | undefined {
    if (planeNormal.dot(lineDirection.normalize()) == 0) {
      return undefined;
    }

    let t =
      (planeNormal.dot(planePoint) - planeNormal.dot(linePoint)) /
      planeNormal.dot(lineDirection.normalize());
    return linePoint.add(lineDirection.normalize().multiplyScalar(t));
  }

  private setupSession(session: XRSession) {
    session.addEventListener('selectstart', (e) => this.onSelectStart(e as XRInputSourceEvent));
    session.addEventListener('selectend', (e) => this.onSelectEnd(e as XRInputSourceEvent));

    this.xr.setReferenceSpaceType('local');

    session.requestReferenceSpace('viewer').then((referenceSpace) => {
      let options: any = {
        space: referenceSpace,
        entityTypes: ['plane'],
      };
      session.requestHitTestSource(options).then((hitTestSource) => {
        this.viewerHitTestSource = hitTestSource;
      });
    });

    session.requestReferenceSpace('local').then((referenceSpace) => {
      this.xrRefSpace = referenceSpace;
    });

    this.xr.setSession(session);
    this.currentSession = session;
    this.onSessionStart();
  }

  private onSelectStart(_event: XRInputSourceEvent) {
    // this.parseEventTransform(event);
  }

  private onSelectEnd(_event: XRInputSourceEvent) {
    // this.parseEventTransform(event);
  }

  // private parseEventTransform(event: XRInputSourceEvent) {
  //   let targetRayPose = event.frame.getPose(
  //     event.inputSource.targetRaySpace,
  //     this.xrRefSpace as EventTarget
  //   );
  //   if (targetRayPose) {
  //     this.calculateInputTransform(targetRayPose.transform);
  //   }
  // }

  private calculateInputTransform(transform: XRRigidTransform) {
    let pos = new Vector3();
    let dir = new Vector3();
    let q = new Quaternion(
      transform.orientation.x,
      transform.orientation.y,
      transform.orientation.z,
      transform.orientation.w
    );
    this.updateTransform(transform, q, pos, dir);
    this.inputs.push(new InputTransform(pos, dir));
  }

  private calculateDeviceTransform(transform: XRRigidTransform) {
    let q = new Quaternion(
      transform.orientation.x,
      transform.orientation.y,
      transform.orientation.z,
      transform.orientation.w
    );
    this.updateTransform(transform, q, this.devicePos, this.deviceDir);
    this.deviceRight.set(1, 0, 0);
    this.deviceRight.applyQuaternion(q);
    this.deviceUp.set(0, 1, 0);
    this.deviceUp.applyQuaternion(q);
  }

  // private calculateInputPos(input: InputTransform): Vector3 | null {
  //   if (!this.devicePos || !this.deviceUp) return null;

  //   let tapPos = new Vector3();
  //   tapPos.copy(input.pos).sub(this.devicePos);
  //   tapPos.projectOnVector(this.deviceUp);
  //   return tapPos;
  // }

  private updateTransform(
    transform: XRRigidTransform,
    quaternion: Quaternion,
    targetPos: Vector3,
    targetDir: Vector3
  ) {
    targetPos.set(transform.position.x, transform.position.y, transform.position.z);
    targetDir.set(0, 0, -1);
    targetDir.applyQuaternion(quaternion);
  }
}

class InputTransform {
  constructor(public pos: Vector3, public dir: Vector3) {}
}
